namespace $.$$ {

	export class $shm_projects_goal extends $.$shm_projects_goal {

		auto() {
			return [
				... super.auto(),
				...( this.run_id() ? [ this.run_events().native() ] : [] ),
			]
		}

		requirements_label() {
			return `Требования к новой версии (v${ this.version() }):`
		}

		requirements( next?: string ) {
			const project_id = this.project_id()
			const version = this.version()
			if( next !== undefined ) {
				this.$.$shm_projects_data.requirements_version( project_id, version )
				return this.$.$shm_projects_data.requirements( project_id, next )
			}
			const owner = this.$.$shm_projects_data.requirements_version( project_id )
			if( owner && owner !== version ) return ''
			return this.$.$shm_projects_data.requirements( project_id )
		}

		override ask_questions( next?: boolean ) {
			return this.$.$mol_state_session.value( `shm_projects:${ this.project_id() }:ask_questions`, next ) ?? false
		}

		@ $mol_mem
		active_run() {
			return this.$.$mol_error_fence(
				()=> $mol_wire_sync( $shm_projects_goal_api ).active( this.project_id() ),
				()=> null,
				promise => this.$.$mol_fail_hidden( promise ),
			)
		}

		@ $mol_mem
		run_id( next?: string ) {
			return next ?? this.$.$mol_state_arg.value( 'goal_run' ) ?? this.active_run()?.id ?? ''
		}

		@ $mol_mem
		run_data( next?: $shm_projects_goal_run | null ): $shm_projects_goal_run | null {
			if( next !== undefined ) {
				if( next?.project ) this.project_sync( next.project )
				return next
			}
			const id = this.run_id()
			if( !id ) return null
			const active = this.active_run()
			if( active?.id === id ) {
				if( active.project ) this.project_sync( active.project )
				return active
			}
			this.run_events().native()
			const run = this.$.$mol_error_fence(
				()=> $mol_wire_sync( $shm_projects_goal_api ).run( id ),
				()=> null,
				promise => this.$.$mol_fail_hidden( promise ),
			)
			if( run?.project ) this.project_sync( run.project )
			return run
		}

		project_sync( project: $shm_projects_data_record ) {
			const records = this.$.$shm_projects_data.records()
			const index = records.findIndex( item => item.id === project.id )
			if( index < 0 ) return this.$.$shm_projects_data.records([ ...records, project ])
			if( this.$.$mol_compare_deep( records[ index ], project ) ) return records
			const next = [ ...records ]
			next[ index ] = project
			return this.$.$shm_projects_data.records( next )
		}

		@ $mol_mem
		run_events() {
			const id = this.run_id()
			const events = new this.$.$shm_projects_goal_api_events
			events.id = ()=> id
			events.notify = ()=> run => this.run_receive( run )
			return events
		}

		@ $mol_action
		run_receive( run: $shm_projects_goal_run ) {
			if( run.id !== this.run_id() ) return run
			const previous = this.run_data()
			const finalized = run.deployments.some( deployment =>
				deployment.stage === 'Пререлиз'
					&& deployment.status === 'deployed'
					&& Boolean( deployment.finalized_at )
			)
			if( finalized ) {
				if( run.project ) this.project_sync( run.project )
				this.requirements( '' )
				this.run_id( '' )
				this.run_data( run )
				this.$.$mol_state_arg.value( 'goal_iteration', null )
				this.$.$mol_state_arg.value( 'goal_run', null )
				return run
			}
			this.run_data( run )
			const iteration_completed = run.status === 'done' && previous?.status !== 'done'
			const prerelease_terminal = run.prerelease?.status === 'done'
				|| run.prerelease?.status === 'failed'
				|| run.prerelease?.status === 'stopped'
			const prerelease_was_terminal = previous?.prerelease?.status === 'done'
				|| previous?.prerelease?.status === 'failed'
				|| previous?.prerelease?.status === 'stopped'
			const prerelease_completed = prerelease_terminal && !prerelease_was_terminal
			if( iteration_completed || prerelease_completed ) this.output_mode( 'result' )
			return run
		}

		goal_form() {
			const run = this.run_data()
			const released = Boolean( run?.deployments.some( deployment => deployment.stage === 'Релиз' ) )
			const finalized = Boolean( run?.deployments.some( deployment =>
				deployment.stage === 'Пререлиз'
					&& deployment.status === 'deployed'
					&& Boolean( deployment.finalized_at )
			) )
			const submitted = Boolean( run && run.iteration > 0 && !released && !finalized )
			const prerelease_retry = run?.prerelease?.status === 'failed' || run?.prerelease?.status === 'stopped'
			const prerelease_active = run?.prerelease?.status === 'queued' || run?.prerelease?.status === 'running'
			return [
				...( submitted ? [ this.Timeline() ] : [ this.Requirements_draft() ] ),
				...( submitted && !prerelease_retry && ( run?.status === 'failed' || run?.status === 'stopped' )
					? [ this.Continue() ]
					: [] ),
				...( submitted && run?.status === 'done' && !prerelease_active
					? [ this.Refinement_draft() ]
					: [] ),
				... this.after(),
			]
		}

		goal_result() {
			const run = this.run_data()
			if( !this.result_visible() || !run ) return []
			if( this.prerelease_selected() ) return [ this.Prerelease() ]
			const iteration_index = this.selected_iteration_index()
			return [
				...( iteration_index >= 0 ? [ this.Iterations() ] : [] ),
				...( iteration_index >= 0 && this.other_artifacts().length ? [ this.Artifacts() ] : [] ),
			]
		}

		goal_result_after() {
			const run = this.run_data()
			if( !run ) return []
			if( !this.prerelease_selected() || !run.prerelease ) return []
			if( run.prerelease.status === 'queued' || run.prerelease.status === 'running' ) return [ this.Prerelease_stop() ]
			if( run.prerelease.status === 'failed' || run.prerelease.status === 'stopped' ) return [ this.Prerelease_continue() ]
			return []
		}

		goal_process_clarifications() {
			return this.selected_clarifications().length ? [ this.Clarifications() ] : []
		}

		result_workspace_id() {
			return this.run_data()?.workspace?.id ?? 'base'
		}

		override Thinking() {
			return this.Process().Thinking()
		}

		thinking_visible() {
			return ( this.Process() as $shm_projects_goal_chat & { thinking_visible(): boolean } ).thinking_visible()
		}

		result_visible() {
			const run = this.run_data()
			return Boolean( run && run.iteration > 0 )
		}

		send_enabled() {
			const status = this.run_data()?.status
			return status !== 'queued' && status !== 'running' && status !== 'waiting'
		}

		selected_clarifications() {
			const selected = this.selected_iteration()
			if( selected?.clarifications?.length ) return selected.clarifications
			return this.selected_iteration_number() === this.run_data()?.iteration
				? this.run_data()?.clarifications ?? []
				: []
		}

		clarifications_editable() {
			const run = this.run_data()
			return Boolean( run?.status === 'waiting' && this.selected_iteration_number() === run.iteration )
		}

		clarifications_content() {
			return [
				this.Clarifications_title(),
				this.Clarifications_list(),
				...( this.clarifications_editable() ? [ this.Clarifications_send() ] : [] ),
			]
		}

		clarification_key( id: string ) {
			return `${ this.selected_iteration_number() }:${ id }`
		}

		clarification_id( key: string ) {
			return key.replace( /^\d+:/, '' )
		}

		clarification_rows() {
			return this.selected_clarifications().map( item => this.Clarification( this.clarification_key( item.id ) ) )
		}

		clarification_question( key: string ) {
			const id = this.clarification_id( key )
			return this.selected_clarifications().find( item => item.id === id )?.question || ''
		}

		@ $mol_mem_key
		clarification_answer( key: string, next?: string ) {
			const id = this.clarification_id( key )
			return next ?? this.selected_clarifications().find( item => item.id === id )?.answer ?? ''
		}

		clarification_answer_content( key: string ) {
			return this.clarifications_editable()
				? this.Clarification_answer( key )
				: this.Clarification_answer_text( key )
		}

		clarifications_send_enabled() {
			const items = this.selected_clarifications()
			return Boolean( items.length && items.every( item => this.clarification_answer( this.clarification_key( item.id ) ).trim() ) )
		}

		@ $mol_action
		override clarifications_send( next?: Event ) {
			if( next === undefined ) return null
			const id = this.run_id()
			const items = this.selected_clarifications()
			if( !id || !items.length ) return next
			const answers = items.map( item => ({ id: item.id, answer: this.clarification_answer( this.clarification_key( item.id ) ).trim() }) )
			if( answers.some( item => !item.answer ) ) return next
			const run = $mol_wire_sync( $shm_projects_goal_api ).clarify( id, answers )
			this.run_data( run )
			this.output_mode( 'process' )
			return next
		}

		@ $mol_action
		override send( next?: Event ) {
			if( next === undefined ) return null
			const goal = this.requirements().trim()
			if( !goal ) return next
			const run = $mol_wire_sync( $shm_projects_goal_api ).start(
				this.project_id(),
				goal,
				this.ask_questions(),
				this.Requirements_draft().attachments_payload(),
			)
			this.requirements_attachments( [] )
			this.run_id( run.id )
			this.run_data( run )
			this.$.$mol_state_arg.value( 'goal_iteration', String( run.iteration ) )
			this.output_mode( 'process' )
			return next
		}

		@ $mol_action
		override continue( next?: Event ) {
			if( next === undefined ) return null
			const id = this.run_id()
			if( !id ) return next
			const run = $mol_wire_sync( $shm_projects_goal_api ).continue( id )
			this.run_data( run )
			this.$.$mol_state_arg.value( 'goal_iteration', String( run.iteration ) )
			this.output_mode( 'process' )
			return next
		}

		@ $mol_mem
		refinement( next?: string ) {
			return next ?? ''
		}

		refinement_label() {
			return `Итерация ${ ( this.run_data()?.iteration ?? 1 ) + 1 } (доработка)`
		}

		@ $mol_action
		override refine( next?: Event ) {
			if( next === undefined ) return null
			const id = this.run_id()
			const goal = this.refinement().trim()
			if( !id || !goal ) return next
			const run = $mol_wire_sync( $shm_projects_goal_api ).refine(
				id,
				goal,
				this.ask_questions(),
				this.Refinement_draft().attachments_payload(),
			)
			this.refinement( '' )
			this.refinement_attachments( [] )
			this.run_data( run )
			this.$.$mol_state_arg.value( 'goal_iteration', String( run.iteration ) )
			this.output_mode( 'process' )
			return next
		}

		timeline_rows() {
			const run = this.run_data()
			if( !run ) return []
			return [
				... Array.from( { length: run.iteration }, ( _, index )=> this.Iteration_status( index + 1 ) ),
				...( run.prerelease ? [ this.Prerelease_status() ] : [] ),
			]
		}

		prerelease_status_content() {
			return [ this.Prerelease_status_head() ]
		}

		prerelease_result_arg() {
			return { goal_iteration: 'prerelease' }
		}

		@ $mol_action
		override prerelease_select( next?: MouseEvent ) {
			if( next === undefined ) return null
			next.preventDefault()
			this.$.$mol_state_arg.value( 'goal_iteration', 'prerelease' )
			const prerelease = this.run_data()?.prerelease
			this.output_mode( prerelease?.status === 'done' ? 'result' : 'process' )
			return next
		}

		prerelease_status_state() {
			return this.prerelease_state()
		}

		prerelease_status_elapsed() {
			const run = this.run_data()
			const prerelease = run?.prerelease
			if( !prerelease ) return this.elapsed_format( 0 )
			if( prerelease.completed_at && run?.timings ) {
				return this.elapsed_format( run.timings.prerelease_ms + run.timings.deployment_ms )
			}
			const completed = prerelease.completed_at
			const elapsed = ( completed ?? this.$.$mol_state_time.now( 1000 ) ) - prerelease.started_at
			return this.elapsed_format( Math.max( 0, elapsed ) )
		}

		iteration_status_content( iteration: number ) {
			return [
				this.Iteration_goal( iteration ),
				this.Iteration_status_head( iteration ),
			]
		}

		iteration_status_rows( iteration: number ) {
			const run = this.run_data()
			return [
				this.Iteration_status_link( iteration ),
				this.Iteration_status_state( iteration ),
				this.Iteration_status_elapsed( iteration ),
				...( iteration === run?.iteration
					&& run.operation !== 'prerelease'
					&& ( run.status === 'queued' || run.status === 'running' || run.status === 'waiting' )
					? [ this.Stop() ] : [] ),
			]
		}

		rollback_allowed() {
			return !this.run_data()?.deployments.some( deployment => deployment.stage === 'Релиз' )
		}

		@ $mol_action
		override iteration_rollback( iteration: number, next?: Event ) {
			if( next === undefined ) return null
			this.rollback_to( iteration )
			return next
		}

		@ $mol_action
		override rollback_all( next?: Event ) {
			if( next === undefined ) return null
			this.rollback_to( 1 )
			return next
		}

		rollback_to( iteration: number ) {
			const id = this.run_id()
			if( !id ) return null
			const run = $mol_wire_sync( $shm_projects_goal_api ).rollback( id, iteration )
			this.refinement( '' )
			if( !run.iteration ) {
				this.run_id( '' )
				this.run_data( null )
				this.$.$mol_state_arg.value( 'goal_iteration', null )
				this.$.$mol_state_arg.value( 'goal_run', null )
				this.output_mode( 'process' )
				return run
			}
			this.run_data( run )
			this.$.$mol_state_arg.value( 'goal_iteration', run.iteration ? String( run.iteration ) : null )
			return run
		}

		iteration_status_title( iteration: number ) {
			return `Итерация ${ iteration }`
		}

		result_iteration_arg( iteration: number ) {
			return { goal_iteration: String( iteration ) }
		}

		@ $mol_action
		override iteration_select( iteration: number, next?: MouseEvent ) {
			if( next === undefined ) return null
			next.preventDefault()
			this.$.$mol_state_arg.value( 'goal_iteration', String( iteration ) )
			return next
		}

		iteration_status_state( iteration: number ) {
			const run = this.run_data()
			if( !run ) return ''
			if( this.result_history().some( item => item.iteration === iteration ) ) return 'Готово'
			if( iteration < run.iteration ) {
				const event = [ ...run.events ].reverse().find( event => event.iteration === iteration )
				if( event?.phase === 'Остановлено' ) return 'Остановлено'
				if( event?.phase === 'Ошибка' ) return 'Ошибка'
				return 'Не завершено'
			}
			if( run.operation === 'prerelease' ) return 'Готово'
			return run.status === 'waiting' ? 'Нужны ответы'
				: run.status === 'queued' || run.status === 'running' ? ( run.operation === 'clarification' ? 'Анализ...' : 'Работа...' )
				: run.status === 'stopped' ? 'Остановлено' : run.status === 'failed' ? 'Ошибка' : 'Готово'
		}

		iteration_status_elapsed( iteration: number ) {
			const run = this.run_data()
			const completed = this.result_history().find( item => item.iteration === iteration )
			const running = iteration === run?.iteration
				&& run.operation !== 'prerelease'
				&& ( run.status === 'queued' || run.status === 'running' )
			const waiting = iteration === run?.iteration
				&& run.operation === 'clarification'
				&& run.status === 'waiting'
			const events = ( run?.events ?? [] ).filter( event => event.iteration === iteration )
			const event_elapsed = events.length > 1 ? events.at( -1 )!.time - events[0].time : 0
			const elapsed = running
				? this.$.$mol_state_time.now( 1000 ) - ( run.iteration_started_at ?? run.started_at )
				: waiting
					? run.updated_at - ( run.iteration_started_at ?? run.started_at )
				: completed?.elapsed_ms
				?? ( completed ? completed.completed_at - ( completed.started_at ?? run?.started_at ?? completed.completed_at ) : event_elapsed )
				?? 0
			return this.elapsed_format( elapsed )
		}

		iteration_status_elapsed_details( iteration: number ) {
			const run = this.run_data()
			const completed = this.result_history().find( item => item.iteration === iteration )
			const timings = completed?.timings ?? ( iteration === run?.iteration ? run.timings : undefined )
			if( !timings ) return ''
			const rows = [
				[ 'Подготовка', timings.workspace_ms + timings.builder_ms + timings.clarification_ms ],
				[ 'Ожидание ответов', timings.waiting_ms ],
				[ 'Работа', timings.implementation_ms ],
				[ 'Проверка', timings.visual_review_ms + timings.checkpoint_ms ],
				[ 'Пререлиз', timings.prerelease_ms + timings.deployment_ms ],
			] as const
			return rows
				.filter( row => row[1] > 0 )
				.map( row => `${ row[0] }: ${ this.elapsed_format( row[1] ).slice( 7 ) }` )
				.join( ' · ' )
		}

		elapsed_format( elapsed: number ) {
			const seconds = Math.floor( elapsed / 1000 )
			const minutes = Math.floor( seconds / 60 )
			return `Время: ${ minutes }:${ String( seconds % 60 ).padStart( 2, '0' ) }`
		}

		iteration_goal_label( iteration: number ) {
			return iteration === 1 ? this.requirements_label() : ''
		}

		iteration_goal_rows( iteration: number ) {
			const field = this.Iteration_goal( iteration )
			return iteration === 1
				? [ field.Label(), field.Content() ]
				: [ field.Content() ]
		}

		iteration_goal_text( iteration: number ) {
			return iteration === 1
				? this.run_data()?.goal || this.requirements()
				: this.run_data()?.refinements[ iteration - 2 ] || ''
		}

		@ $mol_action
		override stop( next?: Event ) {
			if( next === undefined ) return null
			const id = this.run_id()
			if( !id ) return next
			const run = $mol_wire_sync( $shm_projects_goal_api ).stop( id )
			this.run_data( run )
			return run && next
		}

		result_history() {
			const run = this.run_data()
			if( !run ) return []
			if( run.result_history?.length ) return run.result_history
			if( !run.summary && !run.results?.length ) return []
			return [ {
				iteration: run.iteration,
				summary: run.summary,
				started_at: run.iteration_started_at || run.started_at,
				completed_at: run.completed_at || run.updated_at,
				elapsed_ms: ( run.completed_at || run.updated_at ) - ( run.iteration_started_at || run.started_at ),
				changes: this.result_items(),
			} ]
		}

		result_items() {
			const run = this.run_data()
			if( !run ) return []
			if( run.results?.length ) return run.results
			const screenshots = run.artifacts.filter( artifact => artifact.type === 'screenshot' ).map( artifact => artifact.path )
			return run.summary ? [ {
				title: 'Выполненные изменения',
				description: run.summary,
				screenshots,
			} ] : []
		}

		iteration_rows() {
			const index = this.selected_iteration_index()
			return index >= 0 ? [ this.Iteration( index ) ] : []
		}

		selected_iteration() {
			const iteration = this.selected_iteration_number()
			return this.result_history().find( item => item.iteration === iteration ) ?? null
		}

		selected_iteration_number() {
			const run = this.run_data()
			const requested = Number( this.$.$mol_state_arg.value( 'goal_iteration' ) )
			return requested >= 1 && requested <= ( run?.iteration ?? 0 )
				? requested
				: run?.iteration ?? 0
		}

		prerelease_selected() {
			const run = this.run_data()
			if( !run?.prerelease ) return false
			const requested = this.$.$mol_state_arg.value( 'goal_iteration' )
			return requested ? requested === 'prerelease' : run.operation === 'prerelease'
		}

		selected_iteration_index() {
			const selected = this.selected_iteration()
			return selected ? this.result_history().indexOf( selected ) : -1
		}

		iteration_content( index: number ) {
			const changes = index < 0 ? [] : this.result_history()[ index ]?.changes ?? []
			return [
				this.Iteration_title( index ),
				...( changes.length ? [ this.Iteration_changes( index ) ] : [ this.Iteration_summary( index ) ] ),
			]
		}

		iteration_title( index: number ) {
			if( index < 0 ) return `Итерация ${ this.run_data()?.iteration ?? 1 }`
			const item = this.result_history()[ index ]
			if( !item ) return ''
			const date = new Date( item.completed_at ).toLocaleString( 'ru-RU', { dateStyle: 'short', timeStyle: 'short' } )
			return `Итерация ${ item.iteration } · ${ date }`
		}

		iteration_summary( index: number ) {
			if( index < 0 ) return ''
			return this.result_history()[ index ]?.summary || ''
		}

		iteration_event_rows( index: number ) {
			const iteration = index < 0
				? this.run_data()?.iteration
				: this.result_history()[ index ]?.iteration
			return ( this.run_data()?.events ?? [] )
				.map( ( event, event_index )=> ({ event, event_index }) )
				.filter( item => item.event.iteration === iteration && item.event.scope !== 'prerelease' )
				.slice( -8 )
				.map( item => this.Iteration_event( `${ index }:${ item.event_index }` ) )
		}

		iteration_event_text( key: string ) {
			const event_index = Number( key.split( ':' )[ 1 ] )
			return this.event_text_without_images( this.run_data()?.events[ event_index ]?.text || '' )
		}

		event_text_without_images( text: string ) {
			return text.replace( /!\[[^\]]*\]\([^\n)]*\)\s*/g, '' ).trim()
		}

		iteration_change_rows( index: number ) {
			return ( this.result_history()[ index ]?.changes ?? [] )
				.map( ( _, change )=> this.Result_change( `${ index }:${ change }` ) )
		}

		result_change( key: string ) {
			const [ iteration, change ] = key.split( ':' ).map( Number )
			return this.result_history()[ iteration ]?.changes[ change ]
		}

		result_change_content( key: string ) {
			const description = this.result_change_description( key )
			const rows = [
				this.Result_change_title( key ),
				...( description ? [ this.Result_change_description( key ) ] : [] ),
			]
			return this.result_change( key )?.screenshots.length
				? [ ...rows, this.Result_change_screenshots( key ) ]
				: rows
		}

		result_change_title( key: string ) {
			return this.result_change( key )?.title || ''
		}

		result_change_description( key: string ) {
			return this.result_change( key )?.description || ''
		}

		result_change_screenshot_rows( key: string ) {
			return ( this.result_change( key )?.screenshots ?? [] )
				.map( ( _, screenshot )=> this.Result_change_screenshot( `${ key }:${ screenshot }` ) )
		}

		result_change_screenshot_uri( key: string ) {
			const [ iteration, change, screenshot ] = key.split( ':' ).map( Number )
			const path = this.result_history()[ iteration ]?.changes[ change ]?.screenshots[ screenshot ] || ''
			return path ? this.$.$shm_projects_config.resource_url( path ) : ''
		}

		prerelease_content() {
			const prerelease = this.run_data()?.prerelease
			if( !prerelease ) return []
			const meaningful_results = !( prerelease.results.length === 1
				&& prerelease.results[0].title === 'Пререлиз'
				&& prerelease.results[0].description === 'Пререлиз' )
			return [
				this.Prerelease_title(),
				this.Prerelease_state(),
				...( prerelease.external_error_accepted_at ? [ this.Prerelease_external_note() ] : [] ),
				...( prerelease.status === 'failed' ? [ this.Prerelease_summary() ] : [] ),
				...( prerelease.results.length && meaningful_results
					? [ this.Prerelease_changes() ]
					: prerelease.summary && prerelease.status !== 'failed' ? [ this.Prerelease_summary() ] : [] ),
				...( prerelease.status === 'queued' || prerelease.status === 'running' ? [ this.Prerelease_stop() ] : [] ),
				...( prerelease.status === 'failed' || prerelease.status === 'stopped' ? [ this.Prerelease_continue() ] : [] ),
				...( prerelease.status === 'failed' && Boolean( prerelease.summary || prerelease.results.length )
					? [ this.Prerelease_accept_external() ]
					: [] ),
			]
		}

		@ $mol_action
		override prerelease_continue( next?: Event ) {
			if( next === undefined ) return null
			const id = this.run_id()
			if( !id ) return next
			const run = $mol_wire_sync( $shm_projects_goal_api ).prerelease_retry( id )
			this.run_data( run )
			this.$.$mol_state_arg.value( 'goal_iteration', 'prerelease' )
			this.output_mode( run.prerelease?.status === 'done' ? 'result' : 'process' )
			return next
		}

		@ $mol_action
		override prerelease_accept_external( next?: Event ) {
			if( next === undefined ) return null
			const id = this.run_id()
			if( !id ) return next
			const run = $mol_wire_sync( $shm_projects_goal_api ).prerelease_accept_external( id )
			this.run_data( run )
			this.$.$mol_state_arg.value( 'goal_iteration', 'prerelease' )
			this.output_mode( run.prerelease?.status === 'done' ? 'result' : 'process' )
			return next
		}

		prerelease_external_note() {
			const prerelease = this.run_data()?.prerelease
			if( !prerelease?.external_error_accepted_at ) return ''
			return 'Проверка завершена вручную: внешняя ошибка принята пользователем.'
		}

		prerelease_state() {
			const prerelease = this.run_data()?.prerelease
			if( !prerelease ) return ''
			if( prerelease.status === 'queued' || prerelease.status === 'running' ) return 'Работа...'
			if( prerelease.status === 'failed' ) return 'Ошибка'
			if( prerelease.status === 'stopped' ) return 'Остановлено'
			return 'Готово'
		}

		prerelease_event_rows() {
			return ( this.run_data()?.prerelease?.events ?? [] )
				.slice( -8 )
				.map( ( _, index, events )=> this.Prerelease_event( index + Math.max( 0, ( this.run_data()?.prerelease?.events.length ?? 0 ) - events.length ) ) )
		}

		prerelease_event_text( index: number ) {
			return this.event_text_without_images( this.run_data()?.prerelease?.events[ index ]?.text || '' )
		}

		prerelease_summary() {
			const prerelease = this.run_data()?.prerelease
			if( !prerelease ) return ''
			if( prerelease.status === 'failed' ) {
				const reason = ( prerelease.error || this.run_data()?.error || '' ).trim()
				if( reason.includes( 'Failed to connect to 127.0.0.1 port' ) ) {
					return 'Пререлиз не завершён: контейнер запустился, но приложение не начало слушать выделенный порт. Конфигурацию запуска можно исправить и продолжить тот же пререлиз.'
				}
				if( reason.includes( 'untracked working tree files would be overwritten by merge' ) ) {
					return 'Финальная сборка не завершилась: Git не смог обновить временный MAM, потому что базовые файлы рабочего корня были неотслеживаемыми. Это ошибка подготовки рабочего места, а не изменений проекта.'
				}
				return reason ? `Пререлиз не завершён:\n\n${ reason.slice( 0, 2000 ) }` : 'Пререлиз не завершён. Работу можно продолжить.'
			}
			return prerelease.summary || ''
		}

		prerelease_change_rows() {
			return ( this.run_data()?.prerelease?.results ?? [] ).map( ( _, index )=> this.Prerelease_change( index ) )
		}

		prerelease_change( index: number ) {
			return this.run_data()?.prerelease?.results[ index ]
		}

		prerelease_change_content( index: number ) {
			const description = this.prerelease_change_description( index )
			const rows = [
				this.Prerelease_change_title( index ),
				...( description ? [ this.Prerelease_change_description( index ) ] : [] ),
			]
			return this.prerelease_change( index )?.screenshots.length
				? [ ...rows, this.Prerelease_change_screenshots( index ) ]
				: rows
		}

		prerelease_change_title( index: number ) {
			return this.prerelease_change( index )?.title || ''
		}

		prerelease_change_description( index: number ) {
			return this.prerelease_change( index )?.description || ''
		}

		prerelease_change_screenshot_rows( change: number ) {
			return ( this.prerelease_change( change )?.screenshots ?? [] )
				.map( ( _, screenshot )=> this.Prerelease_change_screenshot( `${ change }:${ screenshot }` ) )
		}

		prerelease_change_screenshot_uri( key: string ) {
			const [ change, screenshot ] = key.split( ':' ).map( Number )
			const path = this.prerelease_change( change )?.screenshots[ screenshot ] || ''
			return path ? this.$.$shm_projects_config.resource_url( path ) : ''
		}

		other_artifacts() {
			return ( this.run_data()?.artifacts ?? [] ).filter( artifact => artifact.type === 'video' )
		}

		artifact_rows() {
			return this.other_artifacts().map( ( _, index )=> this.Artifact( index ) )
		}

		artifact_uri( index: number ) {
			const artifact = this.other_artifacts()[ index ]
			return artifact ? this.$.$shm_projects_config.resource_url( artifact.path ) : ''
		}

		artifact_title( index: number ) {
			const artifact = this.other_artifacts()[ index ]
			if( !artifact ) return ''
			const type = artifact.type === 'screenshot' ? 'Скриншот' : artifact.type === 'video' ? 'Видео' : 'Отчёт'
			const number = this.other_artifacts().slice( 0, index + 1 ).filter( item => item.type === artifact.type ).length
			return `${ type } ${ number }`
		}

	}

}
